import React, { createContext, useContext, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, LayoutList, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import mainlogo from "@/assets/mylistiq-logo-new.png";

/**
 * Page frame for the guided (quiz-style) builder.
 * Minimal top bar: logo, "N of M" progress, switch-to-classic and exit.
 */
export interface QuizFrameProps {
  /** 1-based position of the visible question. */
  position: number;
  total: number;
  onExit: () => void;
  onSwitchToClassic: () => void;
  children: React.ReactNode;
  /** Wider container for embedded classic screens. */
  wide?: boolean;
}

/** Lets the active question report its own "N of M" to the frame header. */
const QuizProgressContext = createContext<
  ((p: { position: number; total: number }) => void) | null
>(null);

export const QuizFrame: React.FC<QuizFrameProps> = ({
  position: fallbackPosition,
  total: fallbackTotal,
  onExit,
  onSwitchToClassic,
  children,
  wide = false,
}) => {
  const [reported, setReported] = useState<{ position: number; total: number } | null>(null);
  // Embedded classic screens (wide) don't report, so use the computed values.
  const position = !wide && reported ? reported.position : fallbackPosition;
  const total = !wide && reported ? reported.total : fallbackTotal;
  const pct = Math.min(100, Math.max(4, (position / Math.max(total, 1)) * 100));
  return (
    <div className="relative flex min-h-screen min-w-0 flex-1 flex-col overflow-x-hidden bg-gradient-to-b from-slate-50 via-white to-blue-50/50">
      {/* decorative background */}
      <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 top-1/2 h-80 w-80 rounded-full bg-red-200/20 blur-3xl" />

      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-3 px-4">
          <img src={mainlogo} alt="MyListIQ" className="h-9 w-auto object-contain" />
          <div className="hidden min-w-0 flex-1 flex-col items-center sm:flex">
            <span className="text-xs font-medium text-slate-500">
              {position} of {total}
            </span>
            <div className="mt-1 h-1.5 w-48 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onSwitchToClassic}
              className="h-9 gap-1.5 rounded-full text-xs"
              title="Switch to the classic step-by-step builder"
            >
              <LayoutList className="h-4 w-4" />
              <span className="hidden sm:inline">Classic view</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onExit}
              className="h-9 w-9 rounded-full text-slate-500 hover:text-slate-900"
              title="Exit"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {/* mobile progress */}
        <div className="h-1 w-full bg-slate-200 sm:hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </header>

      <main
        className={cn(
          "relative z-10 mx-auto w-full flex-1 px-4 pb-16 pt-8 sm:pt-12",
          wide ? "max-w-6xl" : "max-w-5xl"
        )}
      >
        <QuizProgressContext.Provider value={setReported}>
          {children}
        </QuizProgressContext.Provider>
      </main>
    </div>
  );
};

export interface QuizQuestionProps {
  position: number;
  total: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  /** Primary "Continue" pill. */
  onContinue?: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  /** Outlined secondary pill ("I'm not sure. Let's skip it."). */
  skipLabel?: string;
  onSkip?: () => void;
  /** Small summary line shown above the action row, e.g. "2 selected". */
  summary?: React.ReactNode;
  /** Max width of the question body. */
  size?: "sm" | "md" | "lg" | "xl";
}

const SIZE: Record<NonNullable<QuizQuestionProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-5xl",
};

/** One question screen: counter, big heading, hint, body and action pills. */
export const QuizQuestion: React.FC<QuizQuestionProps> = ({
  position,
  total,
  title,
  hint,
  children,
  onBack,
  backLabel = "Back",
  onContinue,
  continueLabel = "Continue",
  continueDisabled,
  continueLoading,
  skipLabel,
  onSkip,
  summary,
  size = "lg",
}) => {
  const report = useContext(QuizProgressContext);
  useEffect(() => {
    report?.({ position, total });
  }, [report, position, total]);
  return (
  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="mx-auto mb-8 text-center">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-blue-600 sm:hidden">
        {position} of {total}
      </p>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        {title}
      </h1>
      {hint && <p className="mt-3 text-base text-slate-500">{hint}</p>}
    </div>

    <div className={cn("mx-auto w-full", SIZE[size])}>{children}</div>

    {/* Action bar: Back (left) · Continue (right), optional quiet skip link below */}
    <div className={cn("mx-auto mt-10 w-full", SIZE[size])}>
      {summary && (
        <div className="mb-4 text-center text-sm font-medium text-slate-500">{summary}</div>
      )}
      <div
        className={cn(
          "flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center",
          onBack && onContinue ? "sm:justify-between" : "sm:justify-center"
        )}
      >
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="h-12 rounded-full border-slate-300 bg-white px-6 text-base font-medium text-slate-700 hover:bg-slate-50 sm:min-w-[130px]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLabel}
          </Button>
        )}
        {onContinue && (
          <Button
            type="button"
            onClick={onContinue}
            disabled={continueDisabled || continueLoading}
            className="h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 px-8 text-base font-semibold text-white shadow-lg shadow-blue-500/25 transition hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:shadow-none sm:min-w-[200px]"
          >
            {continueLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {continueLabel}
            {!continueLoading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        )}
      </div>
      {onSkip && (
        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-slate-500 underline-offset-4 transition hover:text-blue-700 hover:underline"
          >
            {skipLabel || "I'm not sure. Let's skip it."}
          </button>
        </div>
      )}
    </div>
  </div>
  );
};
